/**
 * Dashboard-Widget: Mollie KPI (Phase 16 Plan 16-07, REPORT-03)
 *
 * Zeigt 4 Kennzahlen für Mollie-Zahlungen (letzte 30 Tage):
 *   - Offene Zahlungslinks (count + sum)
 *   - Rückbuchungen 30 Tage (count + Gebühr)
 *   - Erfolgsquote 30 Tage (%)
 *   - Ø Checkout bis Bezahlt (h)
 *
 * Verhalten:
 *   - featureMollie=false → rendert null (D-15, komplett verbergen)
 *   - Skeleton während initialem Load (no flash-of-empty)
 *   - Deep-Links: Header→Mollie-Zentrale, Open-Links→OPEN_LINKS-Tab, Chargebacks→Inbox (D-16)
 *
 * Backend-Cache: 60s Redis pro Tenant (D-13).
 */
import React, {FC, memo} from "react";
import {useQuery} from "@apollo/client";
import {Card, Col, Row, Skeleton, Statistic} from "antd";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    LinkOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {Link} from "react-router-dom";
import {Currency, useGetCompanyInfoQuery} from "../../../generated/graphql";
import {URL_CHARGEBACKS, URL_MOLLIE_PAYMENTS} from "../../../models/url";
import {GET_MOLLIE_KPIS} from "../cardMollieKpi.graphql";
import {renderAmount} from "../../../helpers/renderAmount";

type MollieKpiData = {
    getMollieKpis: {
        openLinksCount: number;
        openLinksSum: number;
        chargebacks30dCount: number;
        chargebacks30dFee: number;
        successRate30d: number;
        avgCheckoutToPaidHours: number;
    };
};

type CardMollieKpiProps = {
    style?: React.CSSProperties;
};

const CARD_TITLE = "Mollie-Zahlungen";

// Rundet vor der Ausgabe, weil die Summen aus aggregierten Centbeträgen stammen.
// Die Währung kommt aus den Mandanten-Einstellungen (Default in renderAmount).
const formatMoney = (amount: number): string => renderAmount({amount: Math.round(amount * 100) / 100});

// Chargeback-Gebühren rechnet Mollie in Euro ab, unabhängig von der Mandanten-Währung:
// molliePaymentAttempt.entity.ts dokumentiert chargebackFee ausdrücklich als "Fee in EUR".
const formatFee = (amount: number): string =>
    renderAmount({amount: Math.round(amount * 100) / 100, currency: Currency.Eur});

const CardMollieKpi: FC<CardMollieKpiProps> = ({style}) => {
    const {data: companyInfoData} = useGetCompanyInfoQuery();
    const featureMollie = !!companyInfoData?.companyInfo?.subscription?.featureMollie;

    // D-15: Hook-Order stabil halten — Query läuft, wird aber via skip übersprungen.
    const {data, loading} = useQuery<MollieKpiData>(GET_MOLLIE_KPIS, {
        fetchPolicy: "cache-and-network",
        skip: !featureMollie,
    });

    // D-15: Widget komplett verbergen, wenn Feature nicht aktiviert
    if (!featureMollie) return null;

    const kpi = data && data.getMollieKpis;

    if (loading && !kpi) {
        return (
            <Card style={style} title={CARD_TITLE}>
                <Skeleton active paragraph={{rows: 3}} />
            </Card>
        );
    }
    if (!kpi) return null;

    return (
        <Card
            style={style}
            title={
                <Link to={URL_MOLLIE_PAYMENTS + "?tab=All"}>
                    {CARD_TITLE}
                </Link>
            }
        >
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Link to={URL_MOLLIE_PAYMENTS + "?tab=OpenLinks"}>
                        <Statistic
                            title={"Offene Zahlungslinks"}
                            value={kpi.openLinksCount}
                            suffix={" (" + formatMoney(kpi.openLinksSum) + ")"}
                            prefix={<LinkOutlined />}
                        />
                    </Link>
                </Col>
                <Col span={12}>
                    <Link to={URL_CHARGEBACKS}>
                        <Statistic
                            title={"Rückbuchungen 30 Tage"}
                            value={kpi.chargebacks30dCount}
                            suffix={" (" + formatFee(kpi.chargebacks30dFee) + ")"}
                            prefix={<WarningOutlined />}
                            valueStyle={
                                kpi.chargebacks30dCount > 0
                                    ? {color: "#cf1322"}
                                    : undefined
                            }
                        />
                    </Link>
                </Col>
                <Col span={12}>
                    <Statistic
                        title={"Erfolgsquote 30 Tage"}
                        value={kpi.successRate30d}
                        suffix={" %"}
                        precision={1}
                        prefix={<CheckCircleOutlined />}
                    />
                </Col>
                <Col span={12}>
                    <Statistic
                        title={"Ø Checkout bis Bezahlt"}
                        value={kpi.avgCheckoutToPaidHours}
                        suffix={" h"}
                        precision={1}
                        prefix={<ClockCircleOutlined />}
                    />
                </Col>
            </Row>
        </Card>
    );
};

export default memo(CardMollieKpi);
